import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtTL, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { FileDown, Calendar } from "lucide-react";
import ExportButton from "@/components/ExportButton";

export default function AgingReport() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/cari/aging").then((r) => setRows(r.data));
  }, []);

  const totals = rows.reduce((t, r) => ({
    total: t.total + r.total_debt,
    paid: t.paid + r.paid,
    remaining: t.remaining + r.remaining,
    not_due: t.not_due + r.not_due,
    d_1_30: t.d_1_30 + r.d_1_30,
    d_31_60: t.d_31_60 + r.d_31_60,
    d_61_90: t.d_61_90 + r.d_61_90,
    d_90_plus: t.d_90_plus + r.d_90_plus,
  }), { total: 0, paid: 0, remaining: 0, not_due: 0, d_1_30: 0, d_31_60: 0, d_61_90: 0, d_90_plus: 0 });

  const downloadPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/cari/aging/pdf?auth=${token()}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#6B7280]">
          Toplam <span className="text-[#08285A] font-bold">{rows.length}</span> firma yaşlandırma raporunda
        </div>
        <div className="flex gap-2">
          <ExportButton type="aging" />
          <Button onClick={downloadPdf} data-testid="aging-pdf-btn" className="bg-[#0B3676] hover:bg-[#08285A] text-white">
            <FileDown size={14} className="mr-1" /> PDF İndir
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-3">
        <div className="azd-card p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">Vadesi Gelmemiş</div>
          <div className="text-base font-extrabold text-[#0B3676] mt-1">{fmtTL(totals.not_due)}</div>
        </div>
        <div className="azd-card p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">1-30 Gün</div>
          <div className="text-base font-extrabold text-[#F59E0B] mt-1">{fmtTL(totals.d_1_30)}</div>
        </div>
        <div className="azd-card p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">31-60 Gün</div>
          <div className="text-base font-extrabold text-[#F59E0B] mt-1">{fmtTL(totals.d_31_60)}</div>
        </div>
        <div className="azd-card p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">61-90 Gün</div>
          <div className="text-base font-extrabold text-[#DC2626] mt-1">{fmtTL(totals.d_61_90)}</div>
        </div>
        <div className="azd-card p-4">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">90+ Gün</div>
          <div className="text-base font-extrabold text-[#DC2626] mt-1">{fmtTL(totals.d_90_plus)}</div>
        </div>
      </div>

      <div className="azd-card overflow-hidden">
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[10px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-3 py-3 font-semibold">Firma</th>
                <th className="text-right px-3 py-3 font-semibold">Toplam Borç</th>
                <th className="text-right px-3 py-3 font-semibold">Ödenen</th>
                <th className="text-right px-3 py-3 font-semibold">Kalan</th>
                <th className="text-right px-3 py-3 font-semibold">Vade Gelmemiş</th>
                <th className="text-right px-3 py-3 font-semibold">1-30 G</th>
                <th className="text-right px-3 py-3 font-semibold">31-60 G</th>
                <th className="text-right px-3 py-3 font-semibold">61-90 G</th>
                <th className="text-right px-3 py-3 font-semibold">90+ G</th>
                <th className="text-right px-3 py-3 font-semibold">En Eski Vade</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={10} className="p-8 text-center text-[#6B7280]">Cari kayıt yok</td></tr>
              ) : rows.map((r) => (
                <tr key={r.company_id} className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50"
                    data-testid={`aging-row-${r.company_id}`}>
                  <td className="px-3 py-3 font-semibold text-[#111827]">{r.company_name}</td>
                  <td className="px-3 py-3 text-right text-xs">{fmtTL(r.total_debt)}</td>
                  <td className="px-3 py-3 text-right text-xs text-[#16A34A]">{fmtTL(r.paid)}</td>
                  <td className="px-3 py-3 text-right font-bold text-[#0B3676]">{fmtTL(r.remaining)}</td>
                  <td className="px-3 py-3 text-right text-xs">{r.not_due > 0 ? fmtTL(r.not_due) : "-"}</td>
                  <td className="px-3 py-3 text-right text-xs text-[#F59E0B]">{r.d_1_30 > 0 ? fmtTL(r.d_1_30) : "-"}</td>
                  <td className="px-3 py-3 text-right text-xs text-[#F59E0B]">{r.d_31_60 > 0 ? fmtTL(r.d_31_60) : "-"}</td>
                  <td className="px-3 py-3 text-right text-xs text-[#DC2626] font-semibold">{r.d_61_90 > 0 ? fmtTL(r.d_61_90) : "-"}</td>
                  <td className="px-3 py-3 text-right text-xs text-[#DC2626] font-bold">{r.d_90_plus > 0 ? fmtTL(r.d_90_plus) : "-"}</td>
                  <td className="px-3 py-3 text-right text-xs text-[#6B7280]">{r.oldest_due_date ? fmtDate(r.oldest_due_date) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
